import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Rocket, ArrowRight, Plus, Trash, LinkSimple, CalendarBlank,
  InstagramLogo, FacebookLogo, LinkedinLogo, TwitterLogo,
  PencilSimple, Check, Clock, Eye, ArrowCounterClockwise,
  MagnifyingGlass, Sparkle, X, ArrowSquareOut
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import BrandSettingsSummary from '../components/BrandSettingsSummary';
import ReferenceMediaUpload from '../components/ReferenceMediaUpload';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_ICONS = { instagram: InstagramLogo, facebook: FacebookLogo, linkedin: LinkedinLogo, twitter: TwitterLogo };
const THEME_COLORS = {
  educational: '#22d3ee', promotional: '#f59e0b', behind_the_scenes: '#a78bfa',
  social_proof: '#22c55e', engagement: '#f472b6', inspirational: '#c8f060',
};
const THEME_LABELS = {
  educational: 'Educational', promotional: 'Promotional', behind_the_scenes: 'Behind the Scenes',
  social_proof: 'Social Proof', engagement: 'Engagement', inspirational: 'Inspirational',
};

const LoadingMessages = [
  'Analyzing your online presence...',
  'Understanding your brand voice...',
  'Identifying your target audience...',
  'Studying your products and services...',
  'Creating content themes...',
  'Writing posts in your voice...',
  'Building your content calendar...',
  'Almost ready...',
];

export default function AutoCampaignPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('input'); // input | loading | result | history
  const [urls, setUrls] = useState(['']);
  const [days, setDays] = useState(14); // 3-28 days
  const [loadingMsg, setLoadingMsg] = useState(0);
  const [campaign, setCampaign] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [editBody, setEditBody] = useState('');
  const [viewMode, setViewMode] = useState('calendar'); // calendar | list
  const [imgProgress, setImgProgress] = useState(null); // {ready, total, status}
  const [detailPost, setDetailPost] = useState(null); // for full detail modal
  const [addingToCalendar, setAddingToCalendar] = useState(false);

  const addUrl = () => { if (urls.length < 5) setUrls([...urls, '']); };
  const removeUrl = (i) => setUrls(urls.filter((_, j) => j !== i));
  const updateUrl = (i, val) => { const n = [...urls]; n[i] = val; setUrls(n); };

  const loadCampaigns = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/auto-campaign/list`);
      setCampaigns(data.campaigns || []);
    } catch {}
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  // Poll for image generation progress after a campaign is generated
  useEffect(() => {
    if (!campaign?.campaign_id) return;
    if (campaign.images_status === 'done') return;
    let stopped = false;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/auto-campaign/${campaign.campaign_id}/status`);
        if (stopped) return;
        setImgProgress({ ready: data.ready, failed: data.failed, total: data.total, status: data.images_status });
        // Merge image URLs into campaign posts
        if (data.posts && data.posts.length) {
          const map = {};
          data.posts.forEach(p => { map[p.post_id] = p; });
          setCampaign(prev => prev ? {
            ...prev,
            images_status: data.images_status,
            posts: prev.posts.map(p => map[p.post_id] ? { ...p, image_url: map[p.post_id].image_url, image_status: map[p.post_id].image_status } : p),
          } : prev);
        }
        if (data.images_status !== 'done' && !stopped) {
          setTimeout(poll, 4000);
        }
      } catch {}
    };
    const t = setTimeout(poll, 2000);
    return () => { stopped = true; clearTimeout(t); };
  }, [campaign?.campaign_id, campaign?.images_status]);

  const generate = async () => {
    const validUrls = urls.filter(u => u.trim());
    if (validUrls.length === 0) { toast.error('Add at least one URL'); return; }
    setStep('loading');
    setLoadingMsg(0);
    const interval = setInterval(() => setLoadingMsg(p => Math.min(p + 1, LoadingMessages.length - 1)), 3500);
    try {
      const { data } = await axios.post(`${API}/api/auto-campaign/analyze`, { urls: validUrls, duration_days: days });
      setCampaign(data);
      setStep('result');
      toast.success(`${data.total_posts} posts ready! Visuals are being generated in the background.`);
      loadCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || 'Generation failed');
      setStep('input');
    } finally { clearInterval(interval); }
  };

  const savePostEdit = async () => {
    if (!editingPost || !campaign) return;
    const original = editingPost.body || '';
    try {
      await axios.put(`${API}/api/auto-campaign/${campaign.campaign_id}/post/${editingPost.post_id}`, { body: editBody });
      // Track the edit for Voice DNA refinement (passive learning)
      if (original.trim() !== editBody.trim()) {
        axios.post(`${API}/api/voice/track-edit`, {
          content_type: editingPost.format || 'caption',
          original_text: original,
          edited_text: editBody,
          source_id: editingPost.post_id,
        }).catch(() => {});
      }
      setCampaign(prev => ({
        ...prev,
        posts: prev.posts.map(p => p.post_id === editingPost.post_id ? { ...p, body: editBody } : p),
      }));
      setEditingPost(null);
      setDetailPost(prev => prev && prev.post_id === editingPost.post_id ? { ...prev, body: editBody } : prev);
      toast.success('Post updated');
    } catch { toast.error('Failed to save'); }
  };

  // Fetch FULL campaign data (with all posts) when user clicks a previous campaign card
  const openCampaign = async (c) => {
    try {
      const { data } = await axios.get(`${API}/api/auto-campaign/${c.campaign_id}`);
      setCampaign(data.campaign);
      setStep('result');
    } catch {
      // fallback to whatever data we have
      setCampaign(c);
      setStep('result');
      toast.error('Could not load full campaign');
    }
  };

  const addToCalendar = async () => {
    if (!campaign?.campaign_id || addingToCalendar) return;
    setAddingToCalendar(true);
    try {
      const { data } = await axios.post(`${API}/api/auto-campaign/${campaign.campaign_id}/add-to-calendar`);
      toast.success(`${data.added} posts added to your content calendar`);
      setCampaign(prev => ({ ...prev, added_to_calendar: true }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add to calendar');
    } finally {
      setAddingToCalendar(false);
    }
  };

  // Group posts by week
  const groupByWeek = (posts) => {
    if (!posts) return {};
    const weeks = {};
    posts.forEach(p => {
      const d = new Date(p.date);
      const weekStart = new Date(d); weekStart.setDate(d.getDate() - d.getDay() + 1);
      const key = weekStart.toISOString().slice(0, 10);
      if (!weeks[key]) weeks[key] = [];
      weeks[key].push(p);
    });
    return weeks;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="auto-campaign-page">

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "'Instrument Serif', serif", color: '#f5f0e8' }}>
              Auto Campaign
            </h1>
            <p className="text-sm mt-1" style={{ color: 'rgba(245,240,232,0.35)' }}>
              Paste your links. We'll create a month of ready-to-post content.
            </p>
          </div>
          {step !== 'input' && (
            <div className="flex gap-2">
              <button onClick={() => { setStep('input'); setCampaign(null); }} className="px-4 py-2 rounded-lg text-xs font-medium" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}>
                New Campaign
              </button>
              {campaigns.length > 0 && (
                <button onClick={() => setStep('history')} className="px-4 py-2 rounded-lg text-xs font-medium" style={{ background: 'rgba(255,255,255,0.04)', color: 'rgba(245,240,232,0.5)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  My Campaigns ({campaigns.length})
                </button>
              )}
            </div>
          )}
        </div>

        {/* STEP: INPUT */}
        {step === 'input' && (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            <BrandSettingsSummary
              fields={['brand', 'industry', 'tone', 'audience', 'website']}
              onLoad={(p) => {
                // Pre-fill the first URL from the user's Brand Profile if empty
                if (p?.website) {
                  setUrls((prev) => {
                    const hasContent = (prev || []).some((u) => (u || '').trim().length > 0);
                    if (hasContent) return prev;
                    return [p.website, ...((prev || []).slice(1))];
                  });
                }
              }}
            />
            <div className="rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,240,96,0.06)' }}>
                  <LinkSimple size={20} style={{ color: '#c8f060' }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>Your links</p>
                  <p className="text-xs" style={{ color: 'rgba(245,240,232,0.3)' }}>Paste your website, Instagram, or any social profile</p>
                </div>
              </div>

              <div className="space-y-3">
                {urls.map((url, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={url} onChange={e => updateUrl(i, e.target.value)}
                      placeholder={i === 0 ? 'https://yourbusiness.com or instagram.com/handle' : 'Add another link...'}
                      data-testid={`url-input-${i}`}
                      className="flex-1 px-4 py-3 rounded-xl text-sm bg-transparent outline-none"
                      style={{ border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }} />
                    {urls.length > 1 && (
                      <button onClick={() => removeUrl(i)} className="p-2 rounded-lg hover:bg-red-500/10">
                        <Trash size={16} style={{ color: 'rgba(255,68,68,0.5)' }} />
                      </button>
                    )}
                  </div>
                ))}
                {urls.length < 5 && (
                  <button onClick={addUrl} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium" style={{ border: '1px dashed rgba(255,255,255,0.1)', color: 'rgba(245,240,232,0.3)' }}>
                    <Plus size={14} /> Add another link
                  </button>
                )}
              </div>
            </div>

            <div className="rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
              <p className="text-sm font-semibold mb-3" style={{ color: '#f5f0e8' }}>How much content do you need?</p>
              <div className="flex gap-2 flex-wrap">
                {[
                  { val: 3, label: '3 days', sub: '3 posts' },
                  { val: 5, label: '5 days', sub: '5 posts' },
                  { val: 7, label: '1 week', sub: '5 posts' },
                  { val: 14, label: '2 weeks', sub: '10 posts' },
                  { val: 21, label: '3 weeks', sub: '15 posts' },
                  { val: 28, label: '4 weeks', sub: '20 posts' },
                ].map(opt => (
                  <button key={opt.val} onClick={() => setDays(opt.val)} data-testid={`days-${opt.val}`}
                    className="px-4 py-2.5 rounded-xl text-sm font-medium transition-all flex flex-col items-center min-w-[78px]"
                    style={{
                      background: days === opt.val ? 'rgba(200,240,96,0.1)' : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${days === opt.val ? 'rgba(200,240,96,0.25)' : 'rgba(255,255,255,0.06)'}`,
                      color: days === opt.val ? '#c8f060' : 'rgba(245,240,232,0.4)',
                    }}>
                    <span>{opt.label}</span>
                    <span className="text-[9px] mt-0.5 opacity-60">{opt.sub}</span>
                  </button>
                ))}
              </div>
              <p className="text-xs mt-3" style={{ color: 'rgba(245,240,232,0.2)' }}>
                One post per weekday · {Math.max(1, Math.ceil(days / 5))} credit{Math.max(1, Math.ceil(days / 5)) > 1 ? 's' : ''} · Visuals branded with your colors
              </p>
            </div>

            <ReferenceMediaUpload
              label="Add reference media (optional)"
              note="Upload product photos or brand assets to reference when creating visuals for this campaign."
              acceptedTypes={['image', 'video']}
              maxFiles={3}
              maxSizeMB={25}
            />

            <button onClick={generate} data-testid="generate-campaign-btn"
              className="w-full py-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-transform hover:scale-[1.01]"
              style={{ background: '#c8f060', color: '#080808' }}>
              <Rocket size={18} weight="fill" /> Build my campaign
            </button>

            {/* Previous campaigns */}
            {campaigns.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: 'rgba(245,240,232,0.2)' }}>Previous campaigns</p>
                <div className="space-y-2">
                  {campaigns.slice(0, 5).map(c => (
                    <button key={c.campaign_id} onClick={() => openCampaign(c)}
                      className="w-full text-left p-4 rounded-xl flex items-center gap-3 transition-all hover:ring-1 hover:ring-white/10"
                      style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                      <Sparkle size={16} style={{ color: '#c8f060' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: '#f5f0e8' }}>{c.analysis?.business_name || 'Campaign'}</p>
                        <p className="text-[10px]" style={{ color: 'rgba(245,240,232,0.25)' }}>{c.total_posts} posts · {c.duration_weeks} weeks · {new Date(c.created_at).toLocaleDateString()}</p>
                      </div>
                      <ArrowRight size={14} style={{ color: 'rgba(245,240,232,0.15)' }} />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* STEP: LOADING */}
        {step === 'loading' && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-2 rounded-full animate-spin mb-6" style={{ borderColor: 'rgba(200,240,96,0.15)', borderTopColor: '#c8f060' }} />
            <AnimatePresence mode="wait">
              <motion.p key={loadingMsg} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
                className="text-sm font-medium" style={{ color: 'rgba(245,240,232,0.5)' }}>
                {LoadingMessages[loadingMsg]}
              </motion.p>
            </AnimatePresence>
            <p className="text-xs mt-3" style={{ color: 'rgba(245,240,232,0.15)' }}>This usually takes 30-60 seconds</p>
          </div>
        )}

        {/* STEP: RESULT */}
        {step === 'result' && campaign && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Analysis summary */}
            <div className="rounded-2xl p-5" style={{ background: 'rgba(200,240,96,0.03)', border: '1px solid rgba(200,240,96,0.1)' }}>
              <div className="flex items-center gap-3 mb-3">
                <Sparkle size={18} weight="fill" style={{ color: '#c8f060' }} />
                <p className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>
                  {campaign.analysis?.business_name || 'Your Business'} — {campaign.total_posts} posts ready
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Audience', value: campaign.analysis?.target_audience?.slice(0, 40) || '—' },
                  { label: 'Tone', value: campaign.analysis?.brand_tone || '—' },
                  { label: 'Industry', value: campaign.analysis?.industry || '—' },
                  { label: 'Duration', value: `${campaign.duration_weeks} weeks` },
                ].map((s, i) => (
                  <div key={i}>
                    <p className="text-[9px] uppercase tracking-wider font-semibold" style={{ color: 'rgba(245,240,232,0.2)' }}>{s.label}</p>
                    <p className="text-xs font-medium mt-0.5" style={{ color: 'rgba(245,240,232,0.6)' }}>{s.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* View toggle + Image Generation Progress */}
            <div className="flex gap-2 flex-wrap items-center">
              {['calendar', 'list'].map(v => (
                <button key={v} onClick={() => setViewMode(v)}
                  className="px-4 py-2 rounded-lg text-xs font-medium"
                  style={{
                    background: viewMode === v ? 'rgba(200,240,96,0.08)' : 'transparent',
                    border: `1px solid ${viewMode === v ? 'rgba(200,240,96,0.2)' : 'rgba(255,255,255,0.06)'}`,
                    color: viewMode === v ? '#c8f060' : 'rgba(245,240,232,0.4)',
                  }}>
                  {v === 'calendar' ? 'Calendar View' : 'List View'}
                </button>
              ))}
              {imgProgress && imgProgress.status !== 'done' && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg" data-testid="image-progress-banner"
                  style={{ background: 'rgba(200,240,96,0.06)', border: '1px solid rgba(200,240,96,0.15)' }}>
                  <div className="w-3 h-3 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(200,240,96,0.2)', borderTopColor: '#c8f060' }} />
                  <span className="text-xs font-medium" style={{ color: '#c8f060' }}>
                    Generating visuals: {imgProgress.ready}/{imgProgress.total}
                  </span>
                </div>
              )}
              {imgProgress && imgProgress.status === 'done' && imgProgress.failed > 0 && (
                <button onClick={async () => {
                  toast.info('Retrying failed images...');
                  try {
                    const { data } = await axios.post(`${API}/api/auto-campaign/${campaign.campaign_id}/generate-images`, { count: 10 });
                    toast.success(`${data.generated} images created!`);
                    const { data: updated } = await axios.get(`${API}/api/auto-campaign/${campaign.campaign_id}`);
                    setCampaign(updated.campaign);
                  } catch { toast.error('Retry failed'); }
                }} className="px-3 py-2 rounded-lg text-xs font-medium" data-testid="retry-images-btn"
                  style={{ background: 'rgba(255,180,0,0.06)', color: '#fbbf24', border: '1px solid rgba(255,180,0,0.2)' }}>
                  Retry {imgProgress.failed} failed
                </button>
              )}

              {/* Add to Calendar button */}
              {campaign.added_to_calendar ? (
                <span className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5" data-testid="calendar-added-badge"
                  style={{ background: 'rgba(34,197,94,0.06)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.2)' }}>
                  <Check size={13} weight="bold" /> Added to Calendar
                </span>
              ) : (
                <button onClick={addToCalendar} disabled={addingToCalendar} data-testid="add-to-calendar-btn"
                  className="px-3 py-2 rounded-lg text-xs font-medium flex items-center gap-1.5 disabled:opacity-50"
                  style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.2)' }}>
                  {addingToCalendar ? (
                    <><div className="w-3 h-3 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(200,240,96,0.2)', borderTopColor: '#c8f060' }} /> Adding…</>
                  ) : (
                    <><CalendarBlank size={13} weight="fill" /> Add all to calendar</>
                  )}
                </button>
              )}
            </div>

            {/* Calendar view */}
            {viewMode === 'calendar' && (
              <div className="space-y-6">
                {Object.entries(groupByWeek(campaign.posts)).map(([weekKey, posts], wi) => (
                  <div key={weekKey}>
                    <p className="text-xs uppercase tracking-widest font-semibold mb-3" style={{ color: 'rgba(245,240,232,0.2)' }}>
                      Week {wi + 1} — {new Date(weekKey).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {posts.map(post => {
                        const PlatIcon = PLATFORM_ICONS[post.platform] || InstagramLogo;
                        const themeColor = THEME_COLORS[post.theme] || '#c8f060';
                        return (
                          <div key={post.post_id}
                            onClick={() => setDetailPost(post)}
                            className="rounded-xl overflow-hidden group transition-all hover:ring-1 hover:ring-white/10 cursor-pointer"
                            style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }} data-testid={`post-${post.post_id}`}>
                            {post.image_url ? (
                              <div className="w-full h-32 overflow-hidden">
                                <img src={post.image_url.startsWith('/') ? `${API}${post.image_url}` : post.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                              </div>
                            ) : post.image_status === 'generating' || post.image_status === 'pending' ? (
                              <div className="w-full h-32 flex items-center justify-center" style={{ background: 'rgba(200,240,96,0.04)', borderBottom: '1px solid rgba(255,255,255,0.04)' }} data-testid="image-placeholder">
                                <div className="flex flex-col items-center gap-1.5">
                                  <div className="w-4 h-4 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(200,240,96,0.2)', borderTopColor: '#c8f060' }} />
                                  <span className="text-[9px] font-medium" style={{ color: 'rgba(200,240,96,0.5)' }}>
                                    {post.image_status === 'generating' ? 'Creating visual…' : 'Queued'}
                                  </span>
                                </div>
                              </div>
                            ) : null}
                            <div className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <PlatIcon size={14} style={{ color: 'rgba(245,240,232,0.4)' }} />
                                <span className="text-[10px] font-medium" style={{ color: 'rgba(245,240,232,0.3)' }}>{post.day_name} · {post.date}</span>
                              </div>
                              <span className="text-[8px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: `${themeColor}15`, color: themeColor, border: `1px solid ${themeColor}25` }}>
                                {THEME_LABELS[post.theme] || post.theme}
                              </span>
                            </div>
                            <p className="text-xs font-semibold mb-1.5 break-words" style={{ color: '#f5f0e8', overflowWrap: 'anywhere' }}>{post.hook}</p>
                            <p className="text-[11px] leading-relaxed mb-2 line-clamp-3 break-words" style={{ color: 'rgba(245,240,232,0.4)', overflowWrap: 'anywhere' }}>{post.body}</p>
                            {post.hashtags?.length > 0 && (
                              <p className="text-[9px] mb-2 break-words" style={{ color: 'rgba(200,240,96,0.4)', overflowWrap: 'anywhere' }}>{post.hashtags.map(h => h.startsWith('#') ? h : `#${h}`).join(' ')}</p>
                            )}
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                              <button onClick={(e) => { e.stopPropagation(); setEditingPost(post); setEditBody(post.body); }} className="p-1.5 rounded-md hover:bg-white/5" title="Edit">
                                <PencilSimple size={12} style={{ color: '#c8f060' }} />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(`${post.hook}\n\n${post.body}\n\n${post.hashtags?.map(h => `#${h}`).join(' ') || ''}\n\n${post.cta || ''}`); toast.success('Copied!'); }}
                                className="p-1.5 rounded-md hover:bg-white/5" title="Copy">
                                <Eye size={12} style={{ color: 'rgba(245,240,232,0.4)' }} />
                              </button>
                              <button onClick={(e) => { e.stopPropagation(); navigate('/dashboard/content-studio', { state: { seed: {
                                contentType: post.format === 'reel_script' ? 'reel_script' : post.format === 'carousel_outline' ? 'carousel' : 'caption',
                                topic: post.hook || post.body?.slice(0, 80),
                                tone: campaign?.analysis?.brand_tone || 'Professional',
                                platform: post.platform === 'instagram' ? 'Instagram' : post.platform === 'linkedin' ? 'LinkedIn' : 'Instagram',
                                niche: campaign?.analysis?.industry,
                                audience: campaign?.analysis?.target_audience,
                              } } }); }} className="p-1.5 rounded-md hover:bg-white/5" title="Refine in Content Studio" data-testid={`refine-post-${post.post_id}`}>
                                <ArrowSquareOut size={12} style={{ color: 'rgba(200,240,96,0.7)' }} />
                              </button>
                            </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* List view */}
            {viewMode === 'list' && (
              <div className="space-y-2">
                {campaign.posts.map((post, i) => {
                  const PlatIcon = PLATFORM_ICONS[post.platform] || InstagramLogo;
                  const themeColor = THEME_COLORS[post.theme] || '#c8f060';
                  return (
                    <div key={post.post_id}
                      onClick={() => setDetailPost(post)}
                      className="rounded-xl p-4 flex items-start gap-4 group transition-all hover:ring-1 hover:ring-white/10 cursor-pointer"
                      style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.05)' }}>
                      <div className="text-center shrink-0 w-12">
                        <p className="text-lg font-bold" style={{ color: '#c8f060' }}>{new Date(post.date).getDate()}</p>
                        <p className="text-[9px]" style={{ color: 'rgba(245,240,232,0.25)' }}>{post.day_name?.slice(0, 3)}</p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <PlatIcon size={13} style={{ color: 'rgba(245,240,232,0.4)' }} />
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full font-semibold" style={{ background: `${themeColor}15`, color: themeColor }}>{post.format}</span>
                          <span className="text-[9px] px-1.5 py-0.5 rounded-full" style={{ background: `${themeColor}10`, color: themeColor }}>{THEME_LABELS[post.theme]}</span>
                        </div>
                        <p className="text-sm font-medium mb-0.5 break-words" style={{ color: '#f5f0e8', overflowWrap: 'anywhere' }}>{post.hook}</p>
                        <p className="text-xs line-clamp-2 break-words" style={{ color: 'rgba(245,240,232,0.35)', overflowWrap: 'anywhere' }}>{post.body}</p>
                      </div>
                      <button onClick={(e) => { e.stopPropagation(); setEditingPost(post); setEditBody(post.body); }}
                        className="p-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/5">
                        <PencilSimple size={14} style={{ color: '#c8f060' }} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}

        {/* STEP: HISTORY */}
        {step === 'history' && (
          <div className="space-y-3">
            <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: 'rgba(245,240,232,0.2)' }}>All Campaigns</p>
            {campaigns.map(c => (
              <button key={c.campaign_id} onClick={() => openCampaign(c)}
                className="w-full text-left p-5 rounded-xl flex items-center gap-4 transition-all hover:ring-1 hover:ring-white/10"
                style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
                <Sparkle size={20} style={{ color: '#c8f060' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>{c.analysis?.business_name || 'Campaign'}</p>
                  <p className="text-xs" style={{ color: 'rgba(245,240,232,0.3)' }}>{c.total_posts} posts · {c.duration_weeks} weeks · Created {new Date(c.created_at).toLocaleDateString()}</p>
                </div>
                <ArrowRight size={16} style={{ color: 'rgba(245,240,232,0.2)' }} />
              </button>
            ))}
          </div>
        )}

        {/* Post Detail Modal */}
        <AnimatePresence>
          {detailPost && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.8)' }} onClick={() => setDetailPost(null)} data-testid="post-detail-modal">
              <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
                onClick={e => e.stopPropagation()}
                className="w-full max-w-2xl rounded-2xl overflow-hidden my-auto" style={{ background: '#0a0a0a', border: '1px solid rgba(255,255,255,0.08)' }}>
                {/* Hero image */}
                {detailPost.image_url ? (
                  <div className="w-full" style={{ aspectRatio: '1/1', maxHeight: '480px' }}>
                    <img src={detailPost.image_url.startsWith('/') ? `${API}${detailPost.image_url}` : detailPost.image_url}
                      alt="" className="w-full h-full object-cover" data-testid="detail-hero-image" />
                  </div>
                ) : (
                  <div className="w-full flex items-center justify-center" style={{ aspectRatio: '1/1', maxHeight: '480px', background: 'rgba(200,240,96,0.04)' }}>
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: 'rgba(200,240,96,0.2)', borderTopColor: '#c8f060' }} />
                      <span className="text-xs" style={{ color: 'rgba(200,240,96,0.5)' }}>Visual is being created…</span>
                    </div>
                  </div>
                )}
                <div className="p-6 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'rgba(200,240,96,0.08)', color: '#c8f060' }}>{detailPost.day_name} · {detailPost.date}</span>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(245,240,232,0.5)' }}>{detailPost.platform}</span>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wider" style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(245,240,232,0.5)' }}>{detailPost.format?.replace('_', ' ')}</span>
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: `${THEME_COLORS[detailPost.theme] || '#c8f060'}20`, color: THEME_COLORS[detailPost.theme] || '#c8f060' }}>{THEME_LABELS[detailPost.theme] || detailPost.theme}</span>
                    </div>
                    <button onClick={() => setDetailPost(null)} className="p-1.5 rounded-lg hover:bg-white/5 shrink-0">
                      <X size={16} style={{ color: 'rgba(245,240,232,0.5)' }} />
                    </button>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-semibold mb-1.5" style={{ color: 'rgba(245,240,232,0.25)' }}>Hook</p>
                    <p className="text-lg font-semibold leading-snug break-words" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif", overflowWrap: 'anywhere' }}>{detailPost.hook}</p>
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-widest font-semibold mb-1.5" style={{ color: 'rgba(245,240,232,0.25)' }}>Full post</p>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words" style={{ color: 'rgba(245,240,232,0.7)', overflowWrap: 'anywhere' }}>{detailPost.body}</p>
                  </div>

                  {detailPost.hashtags?.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest font-semibold mb-1.5" style={{ color: 'rgba(245,240,232,0.25)' }}>Hashtags</p>
                      <p className="text-sm break-words" style={{ color: 'rgba(200,240,96,0.7)', overflowWrap: 'anywhere' }}>
                        {detailPost.hashtags.map(h => h.startsWith('#') ? h : `#${h}`).join(' ')}
                      </p>
                    </div>
                  )}

                  {detailPost.cta && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest font-semibold mb-1.5" style={{ color: 'rgba(245,240,232,0.25)' }}>Call to action</p>
                      <p className="text-sm font-medium break-words" style={{ color: '#f5f0e8', overflowWrap: 'anywhere' }}>{detailPost.cta}</p>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                    <button onClick={() => {
                      navigator.clipboard.writeText(`${detailPost.hook}\n\n${detailPost.body}\n\n${detailPost.hashtags?.map(h => h.startsWith('#') ? h : `#${h}`).join(' ') || ''}\n\n${detailPost.cta || ''}`);
                      toast.success('Full post copied!');
                    }} className="px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5" data-testid="detail-copy-btn"
                      style={{ background: '#c8f060', color: '#080808' }}>
                      Copy full post
                    </button>
                    <button onClick={() => { setEditingPost(detailPost); setEditBody(detailPost.body); }}
                      className="px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                      style={{ background: 'rgba(255,255,255,0.04)', color: '#f5f0e8', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <PencilSimple size={13} /> Edit
                    </button>
                    <button onClick={() => navigate('/dashboard/content-studio', { state: { seed: {
                      contentType: detailPost.format === 'reel_script' ? 'reel_script' : detailPost.format === 'carousel_outline' ? 'carousel' : 'caption',
                      topic: detailPost.hook || detailPost.body?.slice(0, 80),
                      tone: campaign?.analysis?.brand_tone || 'Professional',
                      platform: detailPost.platform === 'instagram' ? 'Instagram' : detailPost.platform === 'linkedin' ? 'LinkedIn' : 'Instagram',
                      niche: campaign?.analysis?.industry,
                      audience: campaign?.analysis?.target_audience,
                    } } })}
                      className="px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                      style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.2)' }}>
                      <ArrowSquareOut size={13} /> Refine in Content Studio
                    </button>
                    {detailPost.image_url && (
                      <a href={detailPost.image_url.startsWith('/') ? `${API}${detailPost.image_url}` : detailPost.image_url}
                        download={`post-${detailPost.post_id}.png`} target="_blank" rel="noopener noreferrer"
                        className="px-4 py-2 rounded-lg text-xs font-semibold flex items-center gap-1.5"
                        style={{ background: 'rgba(255,255,255,0.04)', color: '#f5f0e8', border: '1px solid rgba(255,255,255,0.08)' }}>
                        Download visual
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Edit Modal */}
        <AnimatePresence>
          {editingPost && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
              <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }}
                className="w-full max-w-lg rounded-2xl p-6" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>Edit Post — {editingPost.date}</p>
                  <button onClick={() => setEditingPost(null)} className="p-1.5 rounded-lg hover:bg-white/5">
                    <X size={16} style={{ color: 'rgba(245,240,232,0.4)' }} />
                  </button>
                </div>
                <p className="text-xs font-medium mb-2" style={{ color: '#c8f060' }}>{editingPost.hook}</p>
                <textarea value={editBody} onChange={e => setEditBody(e.target.value)} rows={8}
                  className="w-full px-4 py-3 rounded-xl text-sm bg-transparent outline-none resize-none mb-4"
                  style={{ border: '1px solid rgba(255,255,255,0.08)', color: '#f5f0e8' }} />
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditingPost(null)} className="px-4 py-2 rounded-lg text-xs" style={{ color: 'rgba(245,240,232,0.4)' }}>Cancel</button>
                  <button onClick={savePostEdit} className="px-5 py-2 rounded-lg text-xs font-semibold" style={{ background: '#c8f060', color: '#080808' }}>
                    Save changes
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
}
